import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { canFeature } from '@/lib/perms';
import { applyTheme, cachedTheme, cacheTheme } from '@/lib/theme';

/*
  BrandingContext — studio name, logo and color theme, set by admins and applied
  for everyone. Theme is applied at runtime (see lib/theme.js).
*/

const DEFAULT_THEME = { primary: '#3A8C8C', mode: 'light' };
const BrandingContext = createContext(null);

export function BrandingProvider({ children }) {
  const { user } = useAuth();
  const canManage = canFeature(user, 'manageData'); // admin-only editing
  const [branding, setBranding] = useState({
    accountName: 'Interior Story',
    logoUrl: '',
    theme: cachedTheme() || DEFAULT_THEME,
  });

  // Apply the cached theme immediately on mount to avoid a flash of teal.
  useEffect(() => {
    applyTheme(branding.theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(async () => {
    // Branding is public (the login screen shows it too) — no token needed.
    try {
      const res = await api.get('/account/branding');
      const data = { ...res.data, theme: { ...DEFAULT_THEME, ...(res.data.theme || {}) } };
      setBranding(data);
      cacheTheme(data.theme);
      applyTheme(data.theme);
    } catch { /* keep defaults */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const saveBranding = async (payload) => {
    const res = await api.put('/account/branding', payload);
    const data = { ...res.data, theme: { ...DEFAULT_THEME, ...(res.data.theme || {}) } };
    setBranding(data);
    cacheTheme(data.theme);
    applyTheme(data.theme);
    return data;
  };

  // Live preview without persisting (Settings palette hover/selection).
  const previewTheme = (theme) => applyTheme({ ...DEFAULT_THEME, ...theme });
  const resetPreview = () => applyTheme(branding.theme);

  return (
    <BrandingContext.Provider value={{ branding, canManage, refresh, saveBranding, previewTheme, resetPreview }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within a BrandingProvider');
  return ctx;
}
